/**
 * Los ratios de balance (liquidez, fondo de maniobra, endeudamiento, calidad de la deuda) y la
 * estructura 100 % del balance, DENTRO de «Balance de situación», debajo de la fecha (acuerdo de la
 * reunión del 22-10-2026: los estados financieros llevan sus propios ratios; los de la cuenta de
 * resultados van en Pérdidas y ganancias).
 *
 * Pide `/accounting/kpis/balance?at=` con LA MISMA fecha que la tabla: el Balance tiene fecha libre y
 * los ratios se calculan a esa fecha, no al fin de un periodo, así que no pueden enseñar un día y la
 * tabla otro. Los componentes son los de «KPIs financieros» (ScoreCard, panel de detalle).
 */
import { useEffect, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BalanceKpisResponse } from '@jr2os/shared'
import { api, ApiError } from '../../lib/api'
import type { RatioKey } from '../../lib/ratio-insight'
import KpiDrilldownDrawer, { type DrilldownGroup, type KpiDetail } from './KpiDrilldownDrawer'
import { BalanceStructureChart } from './kpis/BalanceStructureChart'
import { ScoreCard } from './kpis/ScoreCard'
import { balanceCardDefs, cardDrill, type KpiCardDef } from './kpis/kpi-cards'

export default function BalanceRatiosAndStructure({ at }: { at: string }) {
  const { t } = useTranslation('accounting')
  const [data, setData] = useState<BalanceKpisResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerTitle, setDrawerTitle] = useState('')
  const [drawerGroups, setDrawerGroups] = useState<DrilldownGroup[]>([])
  const [drawerDetail, setDrawerDetail] = useState<KpiDetail | null>(null)

  useEffect(() => {
    // El campo de fecha puede estar a medio escribir: solo se pide una fecha completa.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(at)) return
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    api
      .get<BalanceKpisResponse>(`/accounting/kpis/balance?at=${at}`)
      .then((res) => {
        if (!ctrl.signal.aborted) setData(res)
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return
        setError(
          err instanceof ApiError
            ? ((err.body as { error?: string })?.error ?? err.message)
            : err instanceof Error
              ? err.message
              : t('errorLoad')
        )
        setData(null)
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false)
      })
    return () => ctrl.abort()
  }, [at, t])

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        <AlertCircle size={16} />
        <span>{error}</span>
      </div>
    )
  }
  if (!data) {
    return loading ? (
      <div className="flex items-center gap-2 text-sm t-muted">
        <Loader2 size={16} className="animate-spin" />
        {t('loading')}
      </div>
    ) : null
  }

  const { balance } = data
  const open = (card: KpiCardDef) => {
    const { title, groups, detail } = cardDrill(card, balance.drilldown, t)
    setDrawerTitle(title)
    setDrawerGroups(groups)
    setDrawerDetail(detail)
    setDrawerOpen(true)
  }

  return (
    <section className={['space-y-4', loading ? 'opacity-70 transition-opacity' : ''].join(' ')}>
      <h2 className="text-sm font-semibold t-secondary uppercase tracking-wider">
        {t('ratiosBalance')}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {balanceCardDefs(balance).map((c) => (
          <ScoreCard
            key={c.key}
            ratio={c.ratio}
            ratioKey={c.key as RatioKey}
            format={c.format}
            onClick={() => open(c)}
          />
        ))}
      </div>

      <BalanceStructureChart vars={balance.vars} />

      <KpiDrilldownDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={drawerTitle}
        detail={drawerDetail}
        groups={drawerGroups}
      />
    </section>
  )
}
